/**
 * Phone-number helpers.
 *
 * `NEXT_PUBLIC_BAKERY_PHONE` is authored for humans and has been set to both
 * national (`0683153629`) and international (`+31 6 12345678`) forms in
 * different environments. `wa.me` accepts neither of those verbatim: it wants
 * digits only, in full international form, with NO leading `+` and NO leading
 * `0`. Passing the national form through produces `wa.me/0683153629`, which
 * WhatsApp rejects with "the phone number shared via link is not on WhatsApp"
 * — a silently dead CTA. Normalise here rather than at each call site.
 */

/** Country calling code for the Netherlands, where the bakery operates. */
const NL_COUNTRY_CODE = '31';

/**
 * Convert a human-authored phone number into the digits-only international
 * form `wa.me` expects.
 *
 * Returns `undefined` when there is nothing usable, so callers can hide the
 * WhatsApp CTA instead of rendering a link that goes nowhere.
 *
 * @example
 * toWhatsAppNumber('0683153629')      // '31683153629'
 * toWhatsAppNumber('+31 6 12345678')  // '31612345678'
 * toWhatsAppNumber('0031612345678')   // '31612345678'
 */
export function toWhatsAppNumber(raw: string | undefined): string | undefined {
  if (!raw) return undefined;

  let digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;

  // `00` is the international dialling prefix; strip it before anything else so
  // `0031…` isn't mistaken for a national number starting with a single `0`.
  if (digits.startsWith('00')) {
    digits = digits.slice(2);
  } else if (digits.startsWith('0')) {
    // National form: drop the trunk `0` and add the country code.
    digits = `${NL_COUNTRY_CODE}${digits.slice(1)}`;
  }

  // Shortest realistic international number is ~8 digits; anything less is a
  // misconfigured env var, and a broken link is worse than no link.
  return digits.length >= 8 ? digits : undefined;
}
